import { faSdg8 } from '@awesome.me/kit-335a9e77a1/icons/kit/custom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartCard, InfoTooltip, LabelText, VerticalBar } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { getReferenceLines } from '../-helpers/getReferenceLines';
import { getTarget } from '../-helpers/getSDGTargetData';
import { useChartData } from '../-hooks/useChartData';

export const Sdg_Emissions1And2RemovedFragment = gql(/* GraphQL */ `
  fragment Sdg_Emissions1And2RemovedFragment on ForestryIndicators {
    emissionsScope1n2_pacePct {
      average
      median
      n
      isDataSuppressed
    }
  }
`);

type Emissions1And2RemovedProps = {
  className?: string;
  indicators: FragmentType<typeof Sdg_Emissions1And2RemovedFragment>;
  viewerIndicators?: FragmentType<typeof Sdg_Emissions1And2RemovedFragment>;
  region: string;
};

export function Emissions1And2Removed({
  indicators,
  viewerIndicators,
  region,
  className,
}: Emissions1And2RemovedProps) {
  const { t } = useTranslation();

  const benchmarkFragment = useFragment(Sdg_Emissions1And2RemovedFragment, indicators);
  const viewerFragment = useFragment(Sdg_Emissions1And2RemovedFragment, viewerIndicators);

  const benchmarkData = benchmarkFragment.emissionsScope1n2_pacePct;
  const viewerData = viewerFragment?.emissionsScope1n2_pacePct;

  const sdgTarget = useMemo(() => getTarget('emissions1And2Removed', region), [region]);
  const referenceLines = getReferenceLines(sdgTarget);

  const { groups, data, yAxisProps } = useChartData({
    viewerData,
    benchmarkData,
    referenceLineValues: referenceLines.map((x) => x.value),
  });

  const titleTooltip = t('emissions1And2Removed_titleTooltip_sdg');

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <div className="flex items-center gap-1.5">
          <FontAwesomeIcon icon={faSdg8} size="2x" className="text-sdg-8" />
          <LabelText>{t('sdgsTargeted_optionLabel_13')}</LabelText>
        </div>
        <ChartCard.Title>
          {t('emissions1And2Removed_title')}
          {titleTooltip && <InfoTooltip className="ml-1">{titleTooltip}</InfoTooltip>}
        </ChartCard.Title>
      </ChartCard.Header>
      <ChartCard.Body>
        <VerticalBar groups={groups} data={data}>
          <VerticalBar.Chart yAxis={yAxisProps} referenceLines={referenceLines} />
          <VerticalBar.Legend className="flex items-center justify-center gap-4" />
        </VerticalBar>
      </ChartCard.Body>
    </ChartCard>
  );
}
